(() => {
  if (window.MetaAdsLanding?.loaded) return;

  const normalize = (value) => String(value || '')
    .trim()
    .toLowerCase()
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

  class MetaAdsCoordinator {
    constructor(hero) {
      this.hero = hero;
      this.messages = new Map();
      this.creatives = new Map();
      this.collections = new Map();
      this.selectedProductId = '';
      this.readCampaigns();
      this.readCollections();
      this.activateCampaign();
    }

    readCampaigns() {
      this.hero.querySelectorAll('[data-meta-ads-message]').forEach((record) => {
        this.messages.set(normalize(record.dataset.utmTerm), {
          eyebrow: record.dataset.eyebrow || '',
          heading: record.dataset.heading || '',
          text: record.dataset.text || ''
        });
      });
      this.hero.querySelectorAll('[data-meta-ads-creative]').forEach((record) => {
        this.creatives.set(normalize(record.dataset.utmContent), {
          collectionHandle: record.dataset.collectionHandle || this.hero.dataset.defaultCollection,
          featuredDesign: normalize(record.dataset.featuredDesign || this.hero.dataset.defaultFeaturedDesign),
          heroImage: record.dataset.heroImage || ''
        });
      });
    }

    readCollections() {
      this.hero.querySelectorAll('[data-meta-ads-collection]').forEach((collectionRecord) => {
        const products = [...collectionRecord.querySelectorAll(':scope > [data-meta-ads-product]')].map((productRecord) => {
          const designs = new Map();
          productRecord.querySelectorAll('[data-meta-ads-design]').forEach((record) => {
            const design = {
              key: normalize(record.dataset.designKey),
              title: record.dataset.title || '',
              skinType: record.dataset.skinType || '',
              sellIndex: record.dataset.sellIndex.trim() === '' ? null : Number(record.dataset.sellIndex),
              variantId: record.dataset.variantId || '',
              available: record.dataset.available === 'true',
              price: record.dataset.price || '',
              url: record.dataset.url || '',
              image: record.dataset.image || '',
              darkImage: record.dataset.darkImage || '',
              imageAlt: record.dataset.imageAlt || '',
              badge: record.dataset.badge || '',
              badgeColor: record.dataset.badgeColor || '',
              badgeTextColor: record.dataset.badgeTextColor || '#ffffff'
            };
            if (design.key && !designs.has(design.key)) designs.set(design.key, design);
          });
          return {
            id: productRecord.dataset.productId || '',
            deviceTitle: productRecord.dataset.deviceTitle || '',
            deviceHandle: productRecord.dataset.deviceHandle || '',
            deviceBadge: productRecord.dataset.deviceBadge || '',
            productUrl: productRecord.dataset.productUrl || '',
            designs
          };
        });
        this.collections.set(collectionRecord.dataset.collectionHandle, {
          id: collectionRecord.dataset.collectionId,
          handle: collectionRecord.dataset.collectionHandle,
          defaultProductId: collectionRecord.dataset.defaultProductId || '',
          products
        });
      });
    }

    activateCampaign() {
      const parameters = new URLSearchParams(window.location.search);
      const termKey = normalize(parameters.get('utm_term') || parameters.get('term'));
      const contentKeys = [...new Set((parameters.get('utm_content') || parameters.get('content') || '').split(',').map(normalize).filter(Boolean))];
      const message = this.messages.get(termKey) || this.messages.get('') || {};
      const creative = this.creatives.get(contentKeys[0]) || this.creatives.get('') || {};
      const featuredDesigns = [...new Set(contentKeys.map(key => this.creatives.get(key)?.featuredDesign || key))];
      this.campaign = { ...creative, ...message, featuredDesigns };
      this.collection = this.collections.get(this.campaign.collectionHandle)
        || this.collections.get(this.hero.dataset.defaultCollection)
        || this.collections.values().next().value;

      let rememberedDevice = '';
      try {
        rememberedDevice = window.localStorage.getItem('mc-device') || '';
      } catch (_error) {
        rememberedDevice = '';
      }
      const rememberedProduct = this.collection?.products.find((product) => (
        product.id === rememberedDevice
        || product.deviceHandle === rememberedDevice
        || product.deviceTitle === rememberedDevice
      ));
      const onlyProduct = this.collection?.products.length === 1 ? this.collection.products[0] : null;
      this.selectedProductId = onlyProduct?.id || rememberedProduct?.id || '';

      const heading = this.hero.querySelector('[data-meta-ads-heading]');
      const text = this.hero.querySelector('[data-meta-ads-text]');
      const eyebrow = this.hero.querySelector('[data-meta-ads-eyebrow]');
      const image = this.hero.querySelector('.meta-ads-hero__media img');
      if (eyebrow) {
        eyebrow.textContent = this.campaign.eyebrow || '';
        eyebrow.hidden = !this.campaign.eyebrow;
      }
      if (heading) heading.textContent = this.campaign.heading || '';
      if (text) text.textContent = this.campaign.text || '';
      if (image && this.campaign.heroImage && image.getAttribute('src') !== this.campaign.heroImage) {
        image.removeAttribute('srcset');
        image.src = this.campaign.heroImage;
      }
      this.publish();
    }

    get activeProduct() {
      if (!this.collection) return null;
      return this.collection.products.find((product) => product.id === this.selectedProductId)
        || this.collection.products.find((product) => product.id === this.collection.defaultProductId)
        || this.collection.products[0]
        || null;
    }

    orderedDesigns() {
      const product = this.activeProduct;
      if (!product) return [];
      const matches = (design, key) => design.key === key || normalize(design.title) === key;
      let featuredDesigns = this.campaign.featuredDesigns.filter(key => [...product.designs.values()].some(design => matches(design, key)));
      if (!featuredDesigns.length) featuredDesigns = [this.campaign.featuredDesign].filter(Boolean);
      const rank = design => {
        const index = featuredDesigns.findIndex(key => matches(design, key));
        return index < 0 ? Infinity : index;
      };
      return [...product.designs.values()].sort((first, second) => {
        if (rank(first) !== rank(second)) return rank(first) - rank(second);
        const firstHasSellIndex = Number.isFinite(first.sellIndex);
        const secondHasSellIndex = Number.isFinite(second.sellIndex);
        if (firstHasSellIndex !== secondHasSellIndex) return firstHasSellIndex ? -1 : 1;
        if (firstHasSellIndex && first.sellIndex !== second.sellIndex) return second.sellIndex - first.sellIndex;
        return first.title.localeCompare(second.title, document.documentElement.lang);
      });
    }

    selectDevice(productId) {
      if (!this.collection?.products.some((product) => product.id === productId)) return;
      if (this.selectedProductId === productId) return;
      const previousProduct = this.selectedProductId ? this.activeProduct : null;
      this.selectedProductId = productId;
      const selectedProduct = this.activeProduct;
      try {
        window.localStorage.setItem('mc-device', selectedProduct.deviceHandle || selectedProduct.deviceTitle || selectedProduct.id);
        window.localStorage.removeItem('mc-device-required');
      } catch (_error) {
        // Device selection still works for the current page when storage is unavailable.
      }
      this.publish();
      window.MarmeladeAnalytics.capture('device selected', {
        surface: 'skins_landing',
        collection_handle: this.collection.handle,
        device_product_id: selectedProduct.id,
        device_handle: selectedProduct.deviceHandle,
        device_title: selectedProduct.deviceTitle,
        previous_device_handle: previousProduct?.deviceHandle || ''
      });
    }

    cardUrl(design) {
      const product = this.activeProduct;
      const device = this.selectedProductId ? product?.deviceHandle : 'unselected';
      return `${design.url}&device=${encodeURIComponent(device || 'unselected')}`;
    }

    state() {
      return {
        collection: this.collection,
        activeProduct: this.activeProduct,
        selectedProductId: this.selectedProductId,
        designs: this.orderedDesigns(),
        cardUrl: (design) => this.cardUrl(design)
      };
    }

    publish() {
      window.MetaAdsLanding.state = this.state();
      window.dispatchEvent(new CustomEvent('meta-ads:state', { detail: window.MetaAdsLanding.state }));
      window.MetaAdsLanding.renderGrids();
    }
  }

  const createCard = (design, cardUrl, eager, darkMode) => {
    const card = document.createElement('a');
    card.className = 'meta-ads-designs__card';
    card.href = cardUrl(design);

    const imageWrap = document.createElement('span');
    imageWrap.className = 'meta-ads-designs__image';
    // Keep the existing image while the dark image catalog is being populated.
    const imageUrl = darkMode && design.darkImage ? design.darkImage : design.image;
    if (imageUrl) {
      const image = document.createElement('img');
      image.src = imageUrl;
      image.alt = design.imageAlt;
      image.width = 900;
      image.height = 900;
      image.loading = eager ? 'eager' : 'lazy';
      imageWrap.append(image);
    }
    if (design.badge) {
      const badge = document.createElement('span');
      badge.className = 'meta-ads-designs__badge';
      badge.textContent = design.badge;
      if (design.badgeColor) badge.style.setProperty('--badge-background', design.badgeColor);
      if (design.badgeTextColor) badge.style.setProperty('--badge-foreground', design.badgeTextColor);
      imageWrap.append(badge);
    }

    const copy = document.createElement('span');
    copy.className = 'meta-ads-designs__copy';
    const title = document.createElement('strong');
    title.textContent = design.title;
    const price = document.createElement('b');
    price.textContent = design.price;
    copy.append(title, price);
    card.append(imageWrap, copy);
    return card;
  };

  class MetaAdsDevicePicker extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';
      this.uiEvents = new AbortController();
      this.trigger = this.querySelector('[data-meta-ads-device-trigger]');
      this.dialog = this.querySelector('[data-meta-ads-device-dialog]');
      this.models = this.querySelector('[data-meta-ads-device-models]');
      this.sheet = new window.MarmeladeFlyout(this.dialog, {
        panel: this.dialog.querySelector(':scope > section'),
        handle: this.dialog.querySelector('[data-meta-ads-device-drag-handle]'),
        dismiss: this.dialog.querySelector('[data-meta-ads-device-drag-dismiss]'),
        backdrop: this.dialog.querySelector('[data-meta-ads-device-close]'),
        initialFocus: () => this.models.querySelector('[aria-pressed="true"]') || this.models.querySelector('button'),
      });
      this.trigger?.addEventListener('click', () => this.open(), { signal: this.uiEvents.signal });
      this.handleState = (event) => this.render(event.detail);
      window.addEventListener('meta-ads:state', this.handleState);
      if (window.MetaAdsLanding.state) this.render(window.MetaAdsLanding.state);
    }

    disconnectedCallback() {
      this.uiEvents?.abort();
      window.removeEventListener('meta-ads:state', this.handleState);
      this.sheet?.destroy();
      delete this.dataset.initialized;
    }

    open() { this.sheet.open(this.trigger); }
    close() { return this.sheet.close(); }

    render(state) {
      if (!state?.collection) return;
      this.hidden = state.collection.products.length <= 1;
      if (this.hidden) {
        this.close();
        return;
      }
      const selected = state.selectedProductId
        ? state.collection.products.find((product) => product.id === state.selectedProductId)
        : null;
      const name = this.querySelector('[data-meta-ads-device-name]');
      const prefix = this.querySelector('[data-meta-ads-device-prefix]');
      const add = this.querySelector('[data-meta-ads-device-add]');
      const replace = this.querySelector('[data-meta-ads-device-replace]');
      if (name) name.textContent = selected?.deviceTitle || this.dataset.defaultLabel;
      if (prefix) prefix.hidden = !selected;
      if (add) add.hidden = Boolean(selected);
      if (replace) replace.hidden = !selected;
      this.trigger?.classList.toggle('is-selected', Boolean(selected));

      if (!this.models) return;
      this.models.replaceChildren();
      state.collection.products.forEach((product) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.classList.toggle('is-active', product.id === state.selectedProductId);
        button.setAttribute('aria-pressed', String(product.id === state.selectedProductId));
        const label = document.createElement('strong');
        label.textContent = product.deviceTitle;
        button.append(label);
        const end = document.createElement('span');
        end.className = 'meta-ads-device__model-end';
        if (product.deviceBadge) {
          const badge = document.createElement('span');
          badge.className = 'meta-ads-device__badge';
          badge.textContent = product.deviceBadge;
          end.append(badge);
        }
        if (product.id === state.selectedProductId) {
          const icon = document.createElement('span');
          icon.className = 'meta-ads-device__check';
          icon.setAttribute('aria-hidden', 'true');
          icon.innerHTML = this.querySelector('[data-meta-ads-check-icon]')?.innerHTML || '';
          end.append(icon);
        }
        if (end.childNodes.length) button.append(end);
        button.addEventListener('click', () => {
          window.MetaAdsLanding.coordinator?.selectDevice(product.id);
          this.close();
        });
        this.models.append(button);
      });
    }
  }

  class MetaAdsDesignGrid extends HTMLElement {
    connectedCallback() {
      window.MetaAdsLanding.renderGrids();
    }

    render(designs, cardUrl, offset, allDesigns, darkMode = false) {
      const grid = this.querySelector('[data-meta-ads-grid]');
      const empty = this.querySelector('[data-meta-ads-empty]');
      if (!grid) return;
      const inserts = new Map();
      const errors = [];
      this.querySelectorAll('[data-meta-ads-inspiration]').forEach((template) => {
        const after = Number(template.dataset.afterCard);
        const key = normalize(template.dataset.designKey);
        const title = normalize(template.dataset.designTitle);
        const exact = allDesigns.filter((design) => design.key === key && key);
        const matches = exact.length ? exact : allDesigns.filter((design) => title && normalize(design.title) === title);
        const design = matches.length === 1 ? matches[0] : null;
        const card = template.content.firstElementChild.cloneNode(true);
        const image = card.querySelector('img');
        if (!Number.isInteger(after) || after < 1 || after > designs.length || !design?.available || !image) {
          console.error('Invalid inspiration tile', this.dataset.sectionId, template.dataset.blockId);
          if (this.dataset.designMode === 'true') {
            card.className = 'meta-ads-designs__configuration-error';
            card.textContent = this.dataset.inspirationError;
            card.setAttribute('role', 'status');
            errors.push(card);
          }
          return;
        }
        card.href = cardUrl(design);
        card.setAttribute('aria-label', design.title);
        card.dataset.designKey = design.key;
        card.dataset.variantId = design.variantId;
        if (!inserts.has(after)) inserts.set(after, []);
        inserts.get(after).push(card);
      });
      const cards = [];
      designs.forEach((design, index) => {
        cards.push(createCard(design, cardUrl, offset + index < 4, darkMode));
        if (inserts.has(index + 1)) cards.push(...inserts.get(index + 1));
      });
      grid.replaceChildren(...cards, ...errors);
      if (empty) empty.hidden = designs.length !== 0;
    }
  }

  window.MetaAdsLanding = {
    loaded: true,
    state: null,
    coordinator: null,
    renderGrids() {
      const state = this.state;
      if (!state) return;
      const darkMode = document.querySelector('meta-ads-hero')?.dataset.skinsColorMode === 'dark';
      let offset = 0;
      document.querySelectorAll('meta-ads-design-grid').forEach((section) => {
        const count = Number(section.dataset.designCount || 4);
        section.render(state.designs.slice(offset, offset + count), state.cardUrl, offset, state.designs, darkMode);
        offset += count;
      });
    },
    initialize() {
      const hero = document.querySelector('meta-ads-hero');
      if (!hero) return;
      this.coordinator = new MetaAdsCoordinator(hero);
    }
  };

  if (!customElements.get('meta-ads-device-picker')) customElements.define('meta-ads-device-picker', MetaAdsDevicePicker);
  if (!customElements.get('meta-ads-design-grid')) customElements.define('meta-ads-design-grid', MetaAdsDesignGrid);

  const initialize = () => window.MetaAdsLanding.initialize();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
  else initialize();
  document.addEventListener('shopify:section:load', initialize);
  document.addEventListener('shopify:section:reorder', () => window.MetaAdsLanding.renderGrids());
  document.addEventListener('shopify:section:unload', () => window.MetaAdsLanding.renderGrids());
})();
